// The public frame: the landing and the venue directory, which a signed-out visitor may browse
// (spec/15 §2.1). A slim top bar with the brand lockup and one call to action that adapts to the
// session — "Sign in" when signed out, "Home" when signed in — so a signed-in visitor browsing venues
// always has a way back. Each public page wraps itself in this.

import { useTranslation } from '@clavian/i18n/react';
import { RouteTransition } from '@clavian/ui-web';
import { Link, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { useSession } from '../api/queries';
import { Wordmark } from './Mark';

export function PublicFrame({ children }: { readonly children: ReactNode }): ReactNode {
  const { t } = useTranslation('player');
  const session = useSession();
  const signedIn = session.data != null;
  const path = useRouterState({ select: (state) => state.location.pathname });

  return (
    <div className="pw-public">
      <a className="pw-skip" href="#pw-main">
        {t('app.skipToContent')}
      </a>
      <header className="pw-topbar pw-topbar--public">
        <div className="pw-topbar__inner">
          <Link to="/" className="pw-topbar__brand" aria-label="Clavian">
            <Wordmark size={26} />
          </Link>
          <div className="pw-topbar__cta">
            <Link to="/venues" className="pw-topnav__item" activeProps={{ 'data-active': 'true' }}>
              {t('nav.venues')}
            </Link>
            {signedIn ? (
              <Link to="/home" className="pw-btn pw-btn--primary pw-btn--sm">
                {t('nav.home')}
              </Link>
            ) : (
              <Link to="/sign-in" className="pw-btn pw-btn--primary pw-btn--sm">
                {t('nav.signIn')}
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="pw-main pw-main--public" id="pw-main">
        <RouteTransition routeKey={path}>{children}</RouteTransition>
      </main>
    </div>
  );
}
